<template>
  <page-outline>
    <template slot="pageContent">
      <div :class="['navigation-container',{ expanded: !isSidebarOpen }]">
        <div>
          <div @click=" isActive = 'createorder'" :class="{ active: isActive == 'createorder' }">
            <i class="fa-light fa-cart-shopping"></i>
            Create Order
          </div>
          <div @click=" isActive = 'payment'" :class="{ active: isActive == 'payment' }">
            <i class="fa-light fa-credit-card"></i>
            Simulate Payment
          </div>
          <div @click=" isActive = 'orderstatus'" :class="{ active: isActive == 'orderstatus' }">
            <i class="fa-light fa-box"></i>
            Order Status
          </div>
        </div>
      </div>
      <div :class="['content bg-light',{ expanded: !isSidebarOpen }]">
        <div v-if="isActive == 'createorder'">
          <create-order-modal @update:status=" isActive = $event"></create-order-modal>
        </div>

        <div v-else-if="isActive == 'payment'">
          <!-- title -->
          <b-container fluid style="display: flex; justify-content: space-between; align-items: center;">
            <div class="content-title">
              <b-row>
                <h1>Payment Simulation</h1>
              </b-row>
              <b-row>
                <span>Create and import payments for testing reconciliation</span>
              </b-row>
            </div>
            <generate-viban-modal></generate-viban-modal>
          </b-container>
          <div class="grid-1-1 gap-2rem">
            <div class="card p-6">
              <span class="subtitle">Add Single Payment</span>
              <span>Create a single payment manually with custom details</span>
              <create-payment-modal :edit="false" :payment="{}"  @update:payments="payments.push($event)"></create-payment-modal>
            </div>
            <div class="card p-6">
              <span class="subtitle">Bulk Import Payments</span>
              <span>Import multiple payments at once from a CSV file</span>
              <import-payment-modal  @update:payments="payments = [...payments, ...$event]"></import-payment-modal>
            </div>
          </div>
          <div class="card p-6">
            <div style="display: flex; justify-content: space-between;">
              <span class="subtitle">Payment Queue ({{payments.length + ' payment' + (payments.length > 1 ? 's': '')}})</span>
              <span class="btn btn-selection">Valid: {{ payments.length }}</span>
            </div>
            <b-table v-if="payments.length > 0" responsive hover rounded
              class="table-header-border rounded mt-2rem"
              style="margin-bottom:0;"
              :items="payments"
              :fields="paymentsFields"
              primary-key="id"
            >
              <template #cell(status)="data">
                <i style="color: #00a63e;" v-if="data.item.status == 1" class="fa-regular fa-circle-check"></i><i style="color: #d4183d;" v-else class="fa-regular fa-circle-exclamation"></i>
              </template>

              <template #cell(VIBAN)="data">
                <span>
                  {{ data.item.VIBAN }}
                </span>
              </template>

              <template #cell(amount)="data">
                <span>
                  €{{ Number(data.item.amount).toFixed(2) }}
                </span>
              </template>

              <template #cell(date)="data">
                <span>
                  {{ formatDate(data.item.date.trim()) }}
                </span>
              </template>

              <template #cell(reference)="data">
                <span>
                  {{ data.item.reference }}
                </span>
              </template>

              <template #cell(actions)="data">
                <div style="display: flex; align-items: center; gap: 1rem; justify-content: center;">
                  <create-payment-modal :edit="true" :payment="data.item" @save="updatePayment(data.item, $event)"></create-payment-modal>
                  <i @click="removePayment(data.item)" class="fa-light fa-trash-can"></i>
                </div>
              </template>
            </b-table>
            <div class="mt-2rem" style="display: flex; justify-content: space-between; align-items: center;" v-if="payments.length > 0">
              <button class="btn-outline-light btn" @click="clearPayments">Clear All Payments</button>
              <button class="btn-outline btn-dark" @click="submitPayment">Submit {{ payments.length }} Valid Payments</button>
            </div>
            <div v-else>
              <div
                v-if="payments.length == 0"
                class="drop-zone mt-2rem"
                :class="{ hover: isDragOver }"
                @dragover.prevent
                @dragenter.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="onDrop"
              >
                <i class="fa-regular fa-arrow-up-from-bracket"></i>
                <span>No payments in queue</span>
                <span>Add payments manually or import from file</span>
              </div>
            </div>
            <alert-modal v-if="isAlertOpen">
              <span><i class="fa-solid fa-circle-check"></i>Successfully submitted {{ this.countNewPayments }} new payment{{ this.countNewPayments > 1 ? 's':'' }}</span>
            </alert-modal>
          </div>
          <div class="info-container">
            <span>How Payment Simulation Works</span>
            <ul>
              <li><span>Add Payment:</span> Create a single payment with a specific VIBAN, amount, date, and reference</li>
              <li><span>Import Payments:</span> Upload a CSV file with multiple payments for batch processing</li>
              <li><span>Payment Queue:</span> Review and edit payments before submission. Both manual and imported payments are added to the queue</li>
              <li><span>VIBAN Validation:</span> Each payment is validated against the VIBAN pool to ensure proper reconciliation</li>
              <li><span>Automatic Processing:</span> After submission, payments appear in the Transaction Ledger and trigger auto-reconciliation</li>
            </ul>
          </div>
        </div>

        <div v-else>
          <!-- title -->
          <b-container fluid>
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div class="content-title">
                <b-row>
                  <h1>Order Status</h1>
                </b-row>
                <b-row>
                  <span>Track all your orders</span>
                </b-row>
              </div>
              <div style="display: flex; gap: 1rem; align-items: center;">
                <button @click="exportSelected()" v-if="selectedItems.length > 0"  class="btn-outline btn-tertiary"><i class="fa-classic fa-arrow-down-to-line"></i>Export Selected ({{ selectedItems.length }})</button>

                <button @click="exportFiltered()" class="btn-outline button-primary"><i class="fa-classic fa-arrow-down-to-line"></i>Export Filtered</button>

                <button @click="exportAll()" class="btn-outline button-primary"><i class="fa-classic fa-arrow-down-to-line"></i>Export All</button>

                <import-order-modal @update:refresh="this.refresh"></import-order-modal>

                <button @click="refresh()" class="btn-outline btn-dark" style="background-color: #155dfc; border: none;">
                  <i :class="['fa-classic', 'fa-arrows-rotate', { 'rotate': isLoading }]"></i>
                  Refresh
                </button>
              </div>
            </div>
          </b-container>
          <div class="card p-6" style="display:grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 1rem;">
            <!-- Search filter -->
            <div style="display: grid;">
              <span class="filter-item-title">Search</span>
              <input type="text" class="filter-item-content" placeholder="Order reference, VIBAN, amount..." @input="event => searchFilter(event.target.value)"/>
            </div>

            <!-- Status filter -->
            <div style="display: grid;">
              <span class="filter-item-title">Status</span>
              <a id="status-picker-link"
                class="filter-item-content d-flex justify-content-between py-2 px-3"
                href="#">
                <span class="fixed-span-size">
                  {{ statusObj.filter(item => {
                    if(item.code == paginationObj.Status){
                      return item
                    }
                  })[0].label }}
                </span>
                <i class="fa-light fa-chevron-down" size="17px" id="action-type-chevron"></i>
              </a>
              <b-popover target="status-picker-link" triggers="focus" placement="bottom" custom-class="popover">
                <div>
                  <span v-for="(item, index) in statusObj" :key="index" :class="['popover-option', {'popover-option-selected':  paginationObj.Status == item.code}]" @click="selectStatus(item.code)">
                    {{item.label}}
                  </span>
                </div>
              </b-popover>
            </div>

            <!-- From date filter -->
            <div style="display: grid;">
              <span style="font-weight: bold;">From Date</span>
              <datetime-picker class="z-1" format="DD/MM/YYYY"
                :class="{'filter-item-input': true, 'filter-item-input-complete': dateBegin, 'error-datepicker': dateBeginError}"
                customInputParentClass="filter-item-content"
                :maxLength="19"
                :placeholderValue="$t('common.datetime_picker_empty_date')"
                inputIcon="fa-solid fa-calendar"
                v-model="dateBegin"
                @input="selectBeginDate">
              </datetime-picker>
              <span v-if="dateBeginError" class="text-danger">{{$t('errors.Data_format_invalid')}}</span>
            </div>

            <!-- Date end filter -->
            <div style="display: grid;">
              <span style="font-weight: bold;">To Date</span>
              <datetime-picker class="z-1" format="DD/MM/YYYY"
                :class="{'filter-item-input': true, 'filter-item-input-complete': dateEnd, 'error-datepicker': dateEndError}"
                customInputParentClass="filter-item-content"
                :maxLength="19"
                :placeholderValue="$t('common.datetime_picker_empty_date')"
                inputIcon="fa-solid fa-calendar"
                v-model="dateEnd"
                @input="selectEndDate">
              </datetime-picker>
              <span v-if="dateEndError" class="text-danger">{{$t('errors.Data_format_invalid')}}</span>
            </div>
          </div>

          <div class="card">
            <div class="p-6" style="display: flex; justify-content: space-between; align-items: center;">
              <div style="display: flex; gap: 1rem; align-items: center;">
                <span class="subtitle">Orders ({{ this.nbTotalResults }})</span>
                <span>Showing {{ paginationObj.firstIndex }} - {{ paginationObj.Status != null || paginationObj.search != null || paginationObj.country != null ? (nbTotalResults < paginationObj.maxResults ? nbTotalResults : currentPage == Math.ceil(nbTotalResults/paginationObj.maxResults) ? nbTotalResults : currentPage * paginationObj.maxResults )
                  :
                  (this.nbTotalResults < paginationObj.maxResults ? this.nbTotalResults : currentPage == Math.ceil(nbTotalResults/paginationObj.maxResults) ? nbTotalResults : currentPage * paginationObj.maxResults ) }} of {{ this.nbTotalResults }} </span>
                <span class="selected" v-if="selectedItems.length > 0">{{ selectedItems.length }} selected</span>
                <button @click="clearSelection()" class="clear" v-if="selectedItems.length > 0"><i class="fa-classic fa-xmark"></i>Clear</button>
              </div>
              <div style="display: flex; gap: 1rem; align-items: center; text-wrap: nowrap;">
                <div class="filter-item" style="flex-direction: row; display: flex; gap: 0.5rem; align-items: center;">
                  <span>Show: </span>
                  <a id="pagination-picker-link"
                    class="filter-item-content d-flex justify-content-between py-2 px-3"
                    href="#">
                    <span class="fixed-span-size">{{ this.paginationObj.maxResults }}</span>
                    <i class="fa-light fa-chevron-down" size="17px" id="page-chevron"></i>
                  </a>
                  <b-popover target="pagination-picker-link" triggers="focus" placement="bottom" custom-class="popover">
                    <div>
                      <span v-for="(resultsPerPage, index) in [10,20,30,40,50]" :key="index" :class="['popover-option', {'popover-option-selected':  resultsPerPage == paginationObj.maxResults}]" @click="selectPagination(resultsPerPage)">
                        {{ resultsPerPage }}
                      </span>
                    </div>
                  </b-popover>
                  <span class="filter-item-title text-ellipsis">Per Page</span>
                </div>
                <span>Last updated: {{ new Date().toLocaleTimeString() }}</span>
              </div>
            </div>
            <hr>
            <div v-if="orders != null">
              <div v-if="orders.length > 0">
                <b-table responsive
                  class="table-header-border mb-0"
                  :items="orders"
                  :fields="fields"
                  primary-key="id"
                >
                  <template #head(checkbox)>
                    <label class="custom-checkbox">
                      <input type="checkbox" 
                        id="all" 
                        name="checkAll"
                        @change="selectAll($event)" />
                      <span class="checkmark"></span>
                    </label>
                  </template>
    
                  <template #cell(checkbox)="data">
                    <label class="custom-checkbox">
                      <input type="checkbox"
                            :value="data.item"
                            @change="toggleSelection($event, data.item)" />
                      <span class="checkmark"></span>
                    </label>
                  </template>
    
                  <template #cell(reference)="data">
                    <span v-if="data.item.Invoices[0]">{{ data.item.Invoices[0].InvoiceIdentifier }}</span>
                    <span v-else>—</span>
                  </template>
    
                  <template #cell(amount)="data">
                    <span v-if="data.item.Amount">€{{ data.item.Amount }}</span>
                    <span v-else>—</span>
                  </template>
    
                  <template #cell(VIBAN)="data">
                    <span v-if="data.item.VIBAN">{{ data.item.VIBAN == '$NotFound' ? '—' : data.item.VIBAN }}</span>
                    <span v-else>—</span>
                  </template>
    
                  <template #cell(status)="data">
                    <span style="color: #334155; background-color: #f8fafc; border-color: #e2e8f0;" class="btn-selection" v-if="data.item.Status == 0">Created</span>
                    <span style="border-color: #ffc9c9; background-color: #ffe2e2; color: #9f0712;" class="btn-selection" v-else-if="data.item.Status == 1">Not Reconciled</span>
                    <span style="color: rgb(161, 98, 7); background-color: rgb(254, 252, 232); border-color: rgb(254, 240, 138);" class="btn-selection" v-else-if="data.item.Status == 2">Partially Paid</span>
                    <span class="btn-selection" v-else-if="data.item.Status == 3">Reconciled</span>
                    <span v-else>—</span>
                  </template>
    
    
                  <template #cell(created)="data">
                    <span v-if="data.item.Invoices[0]">{{  isoToDDMMYYYY(data.item.Invoices[0].InvoicePaymentDate ) }}</span>
                    <span v-else>—</span>
                  </template>
    
                  <template #cell(actions)="data">
                    <detail-modal :order="data.item"></detail-modal>
                  </template>
    
                </b-table>
                <hr>
                <div class="pagination">
                  <button @click="currentPage > 1 && previous()" :class="['btn-outline-light btn', {inactive: currentPage == 1}]">Previous</button>
                  <span>Page {{ currentPage }} of {{ totalPages }}</span>
                  <button @click="currentPage < totalPages && next()" :class="['btn-outline-light btn', {inactive: currentPage == totalPages}]">Next</button>
                </div>
              </div>
              <div v-else>
                <span style="display: flex; justify-content: center; margin: 1rem 0rem; color: #6a7282;">No orders found</span>
              </div>
            </div>
            <div v-else style="margin: 1rem; color: #969696;">
              <span>No results found</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </page-outline>
</template>

<script>
  import PageOutline from '@/components/common/PageOutline.vue'
  import GenerateVIBANModal from '@/components/common/generateVIBANModal.vue';
  import CreatePaymentModal from '@/components/common/createPaymentModal.vue';
  import ImportPaymentModal from '@/components/common/importPaymentModal.vue';
  import DateTimePicker from '@/components/common/dateTimePicker.vue';
  import CreateOrderModal from '@/components/common/createOrderModal.vue';
  import DetailModal from '@/components/common/detailModal.vue';
  import moment from 'moment';
  import ImportOrderModal from '@/components/common/importOrderModal.vue';
  import AlertModal from '@/components/common/alertModal.vue';


  export default {
    name: 'SimulationPage',
    components: {
      'alert-modal': AlertModal,
      'page-outline': PageOutline,
      'generate-viban-modal': GenerateVIBANModal,
      'import-payment-modal': ImportPaymentModal,
      'create-payment-modal': CreatePaymentModal,
      'create-order-modal': CreateOrderModal,
      'datetime-picker': DateTimePicker,
      'import-order-modal': ImportOrderModal,
      'detail-modal': DetailModal
    },
    created (){
      this.$engine.services.payments.getTransactions(this.paginationObj)
      .then(resp => {
        this.nbTotalResults = resp.data.nbTotalResults
        this.orders = resp.data.Transactions
        this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
      })
    },
    data (){
      return {
        fields: [
          { key: 'checkbox', thClass: 'text-center align-middle border-bottom', tdClass: 'text-center align-middle'},
          { key: 'reference', label: 'Order Reference', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left'},
          { key: 'amount', label: 'Amount', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left font-weight-bold'},
          { key: 'VIBAN', label: 'VIBAN', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left'},
          { key: 'status', label: 'Status', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left'},
          { key: 'created', label: 'Created', thClass: 'text-left align-middle border-bottom', tdClass: 'align-middle text-left'},
          { key: 'actions', label: 'Actions', thClass: 'text-left align-middle border-bottom', tdClass: 'text-left align-middle' }
        ],
        paymentsFields: [
          { key: 'status', label: 'Status', thClass: 'text-left align-middle border-bottom border-top border-left', tdClass: 'align-middle text-left border-left border-bottom'},
          { key: 'VIBAN', label: 'VIBAN', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left border-bottom'},
          { key: 'amount', label: 'Amount', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left border-bottom'},
          { key: 'date', label: 'Date', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left border-bottom'},
          { key: 'reference', label: 'Reference', thClass: 'text-left align-middle border-bottom border-top', tdClass: 'align-middle text-left border-bottom'},
          { key: 'actions', label: 'Actions', thClass: 'text-center align-middle border-bottom border-top border-right', tdClass: 'text-center border-right border-bottom text-nowrap' }
        ],
        payments: [],
        payment: {
          clientNumber: null,
          orderReference: null,
          amount: null,
          dueDate: null,
          customerEmail: null,
          clientNumber: null
        },
        orders: [],
        paginationObj: {
          maxResults: 10,
          Status: null,
          FromDate: null,
          ToDate: null,
          Search: null,
          sortOrder: null,
          firstIndex: 1,
          GetDetails: 1,
          Types: "orders"
        },
        statusObj: [
          {
            label: 'All Statuses',
            code: null
          },
          {
            label: 'Created',
            code: 0
          },
          {
            label: 'Payment Received',
            code: 1
          },
          {
            label: 'Payment Confirmed',
            code: 3
          },
          {
            label: 'Partially Paid',
            code: 2
          },
          {
            label: 'Overpaid',
            code: 4
          },
          {
            label: 'Dereconciled',
            code: 5
          }
        ],
        currentPage: 1,
        totalPages: 0,
        isAlertOpen: false,
        isSidebarOpen: true,
        isActive: 'createorder',
        isDragOver: false,
        isLoading: false,
        selectedItems: [],
        showDetails: false,
        dateBegin: '',
        dateBeginError: '',
        dateEnd: '',
        dateEndError: '',
        nbTotalResults: 0,
        countNewPayments: 0
      }
    },
    methods: {
      previous (){
        this.paginationObj.firstIndex = this.paginationObj.firstIndex - this.paginationObj.maxResults
        this.currentPage--
        this.$engine.services.payments.getTransactions(this.paginationObj)
        .then(resp => {
          this.orders = resp.data.Transactions
        })
        .catch(err => {
          console.log(err)
        })
      },
      next (){
        this.paginationObj.firstIndex = this.paginationObj.firstIndex + this.paginationObj.maxResults
        this.currentPage++
        this.$engine.services.payments.getTransactions(this.paginationObj)
        .then(resp => {
          this.orders = resp.data.Transactions
        })
        .catch(err => {
          console.log(err)
        })
        .catch(err => {
          console.log(err)
        })
      },
      onDrop (event){
        this.isDragOver = false
        const file = event.dataTransfer.files[0]
        this.readCSVFile(file)
      },
      clearPayments (){
        this.payments = []
      },
      submitPayment (){
        let requests = this.payments.map(item => {
          const date = this.DDMMYYYYToIso(item.date);
          if(item.status == 1){
            let data = {
              "ExecutionDate": date,
              "Amount": item.amount,
              "VIBAN": item.VIBAN,
              "RefTransaction": item.reference
            }
            return this.$engine.services.payments.createPaymentSimulation(data);
          }
        });

        Promise.all(requests)
        .then(res => {
          console.log(res)
          res.map(r => {
            if(r.data.status == 'OK') ++this.countNewPayments
          })
          if(this.countNewPayments > 0) {
            this.isAlertOpen = true;
            setTimeout(()=>{
              this.isAlertOpen = false;
              this.countNewPayments = 0;
            }, 3000)
          }
          this.clearPayments()
        })
        .catch(err => console.log(err));
      },
      updatePayment(item, updatedItem) {
        const index = this.payments.findIndex(
          payment => payment.id === item.id
        )

        if (index !== -1) {
          this.payments.splice(index, 1, {
            ...this.payments[index],
            ...updatedItem
          })
        }
      },
      removePayment (payment){
        this.payments = this.payments.filter(item => item !== payment)
      },
      exportCSV (filters){
        this.$engine.services.payments.getTransactions(filters)
        .then(resp => {
          const vibanData = resp.data.Transactions
        
          let firstLine = 'Order Reference ; Customer Type ; Client Number ; Customer Email ; Original Amount (EUR) ; Outstanding Amount (EUR) ; Currency ;Transaction Date ; Payment Term ; Allocated VIBAN ; Status ; Created At' + '\r\n'

          let line = ''

          vibanData.forEach(item => {
            line += item.InvoiceIdentifier + ';' + 'Recurring' + ';' + item.Reference1 + ';' + item.customerEmail + ';' + Number(item.Amount).toFixed(2) + ';' + Number(item.Amount).toFixed(2) + ';' + 'EUR' + ';' + new Date(item.CreationDate).toLocaleDateString() + ';' +  this.diffInDays(item.ExecutionDate, item.CreationDate) + ';' + item.VIBAN + ';' + (item.Status == 0 ? 'Created' : item.Status == 1 ? 'Not Reconciled' : item.Status == 2 ? '' : item.Status == 3 ? 'Reconciled' : '') + ';' + new Date(item.CreationDate).toLocaleDateString() +'\r\n'
          })

          let data = firstLine + line
          let linkSource = 'data:text/csv;charset:utf-8;base64,' + btoa(data)
          let downloadLink = document.createElement('a')
          let today = moment()
          let fileName = 'orders_' + today.unix() + '.csv'

          downloadLink.href = linkSource
          downloadLink.download = fileName
          downloadLink.click()
        })
        .catch(err => {
          console.log(err)
        })
      },
      exportAll () {
        this.exportCSV({GetDetails: 1, Types: "orders"})
      },
      exportFiltered () {
        this.exportCSV({
          firstIndex: 1,
          sortOrder: this.paginationObj.sortOrder,
          Search : this.paginationObj.Search,
          FromDate: this.paginationObj.FromDate,
          ToDate: this.paginationObj.ToDate,
          Status: this.paginationObj.Status,
          GetDetails: 1, 
          Types: "orders"
        })
      },
      exportSelected (){
        const selection = [...this.selectedItems];
        let firstLine = 'Order Reference ; Customer Type ; Client Number ; Customer Email ; Original Amount (EUR) ; Outstanding Amount (EUR) ; Currency ;Transaction Date ; Payment Term ; Allocated VIBAN ; Status ; Created At' + '\r\n'

        let line = ''

        selection.forEach(item => {
          line += item.InvoiceIdentifier + ';' +
          'Recurring' + ';' + item.Reference1 + ';' + item.customerEmail + ';' + Number(item.Amount).toFixed(2) + ';' + Number(item.Amount).toFixed(2) + ';' + 'EUR' + ';' + new Date(item.CreationDate).toLocaleDateString() + ';' +  this.diffInDays(item.ExecutionDate, item.CreationDate) + ';' + item.VIBAN + ';' + (item.Status == 0 ? 'Created' : item.Status == 1 ? 'Not Reconciled' : item.Status == 2 ? '' : item.Status == 3 ? 'Reconciled' : '') + ';' + new Date(item.CreationDate).toLocaleDateString() +'\r\n'
        })

        let data = firstLine + line
        let linkSource = 'data:text/csv;charset:utf-8;base64,' + btoa(data)
        let downloadLink = document.createElement('a')
        let today = moment()
        let fileName = 'orders_' + today.unix() + '.csv'

        downloadLink.href = linkSource
        downloadLink.download = fileName
        downloadLink.click()
        this.isAlertOpen = true;
        this.countExported = selection.length
        setTimeout(()=>{
          this.isAlertOpen = false;
          this.countExported = 0
        }, 3000)
      },
      formatDate (dateStr) {
        let datearray = dateStr.split(' ');
        const [day, month, year] = datearray[0].split("/");
        return `${year}-${month}-${day}`;
      },
      refresh (){
        if (this.isLoading) return;
        this.isLoading = true;
        setTimeout(() => {
          this.isLoading = false;
          this.$engine.services.payments.getTransactions(this.paginationObj)
          .then(resp => {
            this.nbTotalResults = resp.data.nbTotalResults
            this.orders = resp.data.Transactions
            this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
          })
        }, 800);
      },
      readCSVFile (file) {
        if (!file || !file.name.endsWith('.csv')) return

        const reader = new FileReader()
        reader.onload = (e) => {
          const csvText = e.target.result
          const fileContent = csvText
            .trim()
            .split('\r\n')
            .filter(item => item && item !== 'viban ; amount ; date ; reference')

          fileContent.forEach(el => {
            let elements = el.split(';')
            let VIBAN = elements[0]
            let amount = elements[1]
            let date = elements[2]
            let reference = elements[3]

            let data = {
              VIBAN: VIBAN,
              amount: amount,
              date: date,
              reference: reference
            }
            
            const sanitized = this.sanitize(data.VIBAN)
            if (!sanitized) return

            this.$engine.services.payments.getGroups({ search : sanitized , maxResults : 200})
            .then(res => {
              if(res.data.references.length > 0){
                this.payments.push({
                  amount: Number(data.amount),
                  date: new Date().toLocaleDateString(),
                  reference: data.reference,
                  VIBAN: data.VIBAN,
                  status: 1
                })
              }
            })
            .catch(err => console.log(err))
          })
        }
        reader.readAsText(file)
      },
      sanitize (value) {
        if (typeof value !== 'string') return ''

        return value
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9-_]/g, '')
          .replace(/[\u200B-\u200D\uFEFF]/g, '')
          .replace(/\r?\n|\r/g, '')
      },
      toggleSelection (event, value) {
        const checked = event.target.checked;

        if (checked) {
          if (!this.selectedItems.some(item => item == value)) {
            this.selectedItems.push(value)
          }
        } else {
          const checkbox = document.querySelectorAll('input[type="checkbox"]')[0]
          if(checkbox.checked){
            checkbox.checked = false
          }
          this.selectedItems = this.selectedItems.filter(item => item !== value);
        }
      },
      selectAll (event){
        const checked = event.target.checked;
        const checkboxes = document.querySelectorAll('input[type="checkbox"]')
        if (checked) {
          checkboxes.forEach(item => item.checked = true)
          this.selectedItems = [...this.orders];
        } else {
          this.selectedItems = []
          checkboxes.forEach(item => item.checked = false)
        }
      },
      clearSelection () {
        this.selectedItems = []
        const checkboxes = document.querySelectorAll('input[type="checkbox"]')
        checkboxes.forEach(item => item.checked = false)
      },
      isoToDDMMYYYY (isoDate) {
        const [year, month, day] = isoDate.split("T")[0].split("-");
        return `${day}/${month}/${year}`;
      },
      DDMMYYYYToIso (date) {
        const dateSplit = date.split(" ");
        const [day, month, year] = dateSplit[0].split("/");
        return new Date(`${year}-${month}-${day}`);
      },
      diffInDays (executionDate, creationDate){
        const ms = (1000 * 60 * 60 * 24)
        const days = Math.floor((new Date(executionDate) - new Date(creationDate)) / ms)+ 1
        if(days > 0) {
          return `Net ${days}`
        } else if (days == 0) {
          return 'Instant'
        } else {
          return days
        }
      },
      searchFilter (search){
        this.paginationObj.firstIndex = 1
        this.paginationObj.Search = search

        this.$engine.services.payments.getTransactions(this.paginationObj)
        .then(resp => {
          this.orders = resp.data.Transactions
          this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
        })
        .catch(err => {
          console.log(err)
        })
      },
      selectStatus (status){
        this.paginationObj.firstIndex = 1
        this.paginationObj.Status = status

        this.$engine.services.payments.getTransactions(this.paginationObj)
        .then(resp => {
          this.orders = resp.data.Transactions
          this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
        })
        .catch(err => {
          console.log(err)
        })
      },
      selectBeginDate (date){
        console.log(this.formatDate(date))
        if(!date){
          this.paginationObj.FromDate = null
           this.$engine.services.payments.getTransactions(this.paginationObj)
          .then(resp => {
            this.nbTotalResults = resp.data.nbTotalResults
            this.orders = resp.data.Transactions
            this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
          })
          return
        }
        
        this.paginationObj.firstIndex = 1
        this.paginationObj.FromDate = new Date(this.formatDate(date)).toISOString();
        
        this.$engine.services.payments.getTransactions(this.paginationObj)
        .then(resp => {
          console.log(resp.data)
          this.orders = resp.data.Transactions
          this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
        })
        .catch(err => {
          console.log(err)
        })
      },
      selectEndDate (date){
        if(!date){
          this.paginationObj.ToDate = null
           this.$engine.services.payments.getTransactions(this.paginationObj)
          .then(resp => {
            this.nbTotalResults = resp.data.nbTotalResults
            this.orders = resp.data.Transactions
            this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
          })
          return
        }

        this.paginationObj.firstIndex = 1
        this.paginationObj.ToDate = this.formatDate(date)

        this.$engine.services.payments.getTransactions(this.paginationObj)
        .then(resp => {
          this.orders = resp.data.Transactions
          this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
        })
        .catch(err => {
          console.log(err)
        })
      },
      selectPagination (resultsPerPage){
        this.paginationObj.firstIndex = 1
        this.paginationObj.maxResults = resultsPerPage

        this.$engine.services.payments.getTransactions(this.paginationObj)
        .then(resp => {
          this.orders = resp.data.Transactions
          this.nbTotalResults = resp.data.nbTotalResults
          this.totalPages = Math.ceil(resp.data.nbTotalResults/this.paginationObj.maxResults)
        })
        .catch(err => {
          console.log(err)
        })
      },
    }
  }
</script>

<style scoped>

.rotate {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.inactive{
  color: #0a0a0a;
  opacity: .5;;
  pointer-events: none;
  outline-style: none;
}

.pagination{
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #000000a6;
  padding: 1rem 2rem;
}

.selected {
  padding: 0.5rem;
  font-weight: bold;
  border-radius: calc(0.625rem - 2px);
  background-color: #dbeafe;
  color: #1d4ed8;
}

.clear{
  cursor: pointer;
  padding: 0.5rem;
  font-weight: bold;
  border-radius: calc(0.625rem - 2px);
  border: none;
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: transparent;
}

.clear:hover{
  background-color: #e9ebef;
}

.btn-selection{
  font-weight: bold !important;
  min-height: 0.5rem !important;
  gap: 1rem;
  padding: 0.25rem 0.5rem !important;
  align-items: center;
  border: 1px solid #b9f8cf;
  background-color: #f0fdf4;
  border-radius: calc(0.75rem - 2px);
  color: #008236;
  font-size: 0.85rem;
  cursor: default !important;
}

small{
  color: #6a7282;
  font-size: 0.85rem;
  margin: 0.25rem 0rem;
}

.drop-zone{
  row-gap: 1rem;
  border: 2px dashed #d1d5dc;
  background-color: #f8f8f8;
  border-radius: 0.625rem;
  padding: calc(.25rem * 12);
  display: grid !important;
  justify-content: center !important;
  align-items: center !important;
}


.drop-zone.hover {
  border-color: #155dfc;
  background-color: #eff6ff;
}
  
.drop-zone>i{
  display: flex;
  justify-content: center;
  font-size: calc(.25rem * 12);
  font-weight: bold;
  color: #99a1af;
  margin: 1rem 0rem;
}

.drop-zone>span{
  display: flex;
  justify-content: center;
  color: #6a7282;
}

.drop-zone:hover{
  border-color: #99a1af;
}

.drop-zone>div{
  display: flex;
  justify-content: center;
}

ul{
  padding-left: 1rem;
}

.subtitle{
  font-size: 1.25rem;
}

.card>div>ul>li::marker{
  color: #155dfc;
}

.info-container{
  display: grid;
  gap: 0rem 1rem;
  padding: calc(.25rem * 4);
  background-color: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1c398e;
  border-radius: .625rem;
  margin: 1rem 0rem;
}

.info-container>ul>li>span{
  font-weight: bold;
}

.info-container>span{
  font-weight: bold;
  font-size: 1.25rem;
  margin: 0rem 0rem 2rem 0rem;
}

.navigation-container{
  min-height: 4rem;
  position: sticky;
  padding: 0rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1019607843);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1019607843), 0 1px 2px -1px rgba(0, 0, 0, 0.1019607843);
  background-color: #fff;
}

.navigation-container>div{
  padding: 0rem 8rem;
  display: flex;
}

.navigation-container>div>div{
  padding: 1rem;
  width: 100%;
  gap: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 4rem;
  font-weight: bold;
  cursor: pointer;
  color: #6a7282;
  border-bottom: 2px solid transparent;
}

.navigation-container>div>div.active{
  color: #155dfc;
  border-bottom: 2px solid #155dfc;
}

.navigation-container>div>div.active:hover{
  color: #155dfc;
  border-bottom: 2px solid #155dfc;
}

.navigation-container>div>div:hover{
  color: #364153;
  border-bottom: 2px solid #d1d5dc;
}

.navigation-container>div>div>span{
  background-color: #eff6ff;
  font-size: 1.5rem;
  font-weight: lighter;
  padding: 0.3rem;
  border-radius: calc(0.625rem - 2px);
  width: 8rem;
  white-space: nowrap;
}

.navigation-container>div>div>i{
  font-size: 1.5rem;
}

.grid-2-1 {
  display: grid;
  grid-template-columns: 2fr 1fr;
}
.grid-3-1 {
  display: grid;
  grid-template-columns: 3fr 1fr;
}
.grid-1-1 {
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.gap-1rem {
  gap: 1rem;
}
.gap-2rem {
  gap: 2rem;
}
.gap-0-2rem {
  gap: 0rem 2rem;
}
.mt-2rem {
  margin-top: 2rem;
}
.mb-0 {
  margin-bottom: 0;
}
</style>
